import type { AggregatedHolding, BrokerAccount, PositionRule } from "../../types";
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { apiService } from '../../services/api';

interface EnhancedHoldingsTableProps {
  holdings: AggregatedHolding[];
  brokerAccounts: BrokerAccount[];
  rules: PositionRule[];
  onAddRule: (rule: Partial<PositionRule>) => void;
  onBulkSaved?: () => void;
  searchTerm: string;
  selectedAccount: string;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  onDeleteRule: (ruleId: string) => void;
  onDeleteAggregatedHolding?: (symbol: string, exchange: string) => void;
  autoExpandedSymbols?: Set<string>;
  onAddNew?: () => void;
  inlineFormSlot?: React.ReactNode;
}

const DEFAULT_MAGIC_RULE = {
  action_type: 'ALERT' as const,
  trigger_type: 'TRAILING_STOP_LOSS' as const,
  trigger_value: 7,
  value_type: 'PERCENT' as const,
};

const EnhancedHoldingsTable = forwardRef<any, EnhancedHoldingsTableProps>(({ 
  holdings,
  rules,
  onAddRule,
  onBulkSaved,
  searchTerm,
  selectedAccount,
  sortField,
  sortDirection,
  onSort,
  onDeleteRule,
  onDeleteAggregatedHolding,
  autoExpandedSymbols = new Set(),
  onAddNew,
  inlineFormSlot
}, ref) => {
  const [expandedSymbols, setExpandedSymbols] = useState<Set<string>>(new Set());
  const [openFormKeys, setOpenFormKeys] = useState<Set<string>>(new Set());
  const [ruleDrafts, setRuleDrafts] = useState<Record<string, Partial<PositionRule>>>({});
  const [error, setError] = useState<string | null>(null);
  const [highlightedSymbols, setHighlightedSymbols] = useState<Set<string>>(new Set());
  const tableRef = React.useRef<HTMLDivElement>(null);
  
  // State for inline editing of holdings
  const [editingFields, setEditingFields] = useState<Set<string>>(new Set());
  const [editingValues, setEditingValues] = useState<Record<string, { quantity?: string; average_price?: string }>>({});
  const [updatingFields, setUpdatingFields] = useState<Set<string>>(new Set());

  // Handle auto-expanded symbols from parent
  useEffect(() => {
    if (autoExpandedSymbols.size > 0) {
      setExpandedSymbols(autoExpandedSymbols);
    }
  }, [autoExpandedSymbols]);

  // const [pendingRules, setPendingRules] = useState<Record<string, Partial<PositionRule>>>({});

  // Magic Setup: expand all, pre-populate 5% trailing alert for each holding/account with no rules
  const handleMagicSetup = () => {
    const newExpanded = new Set<string>();
    const newOpenKeys = new Set<string>(openFormKeys);
    const newDrafts = { ...ruleDrafts };
    const newHighlighted = new Set<string>();

    holdings.forEach(holding => {
      const key = `${holding.symbol}-${holding.exchange}`;
      newExpanded.add(key);

      // For each account in the holding
      holding.accounts.forEach(account => {
        const ruleKey = `${holding.symbol}-${holding.exchange}-${account.broker_account_id}`;
        const hasRule = rules.some(rule =>
          rule.symbol === holding.symbol &&
          rule.exchange === holding.exchange &&
          rule.target_account_id === account.broker_account_id
        );

        if (!hasRule && !newDrafts[ruleKey]) {
          newOpenKeys.add(ruleKey);
          newDrafts[ruleKey] = {
            ...DEFAULT_MAGIC_RULE,
            symbol: holding.symbol,
            exchange: holding.exchange,
            target_account_id: account.broker_account_id,
            rule_scope: 'specific_account',
          };
          // Add to highlighted symbols
          newHighlighted.add(key);
        }
      });
    });
    setExpandedSymbols(newExpanded);
    setOpenFormKeys(newOpenKeys);
    setRuleDrafts(newDrafts);
    setHighlightedSymbols(newHighlighted);

    // Scroll to first highlighted row after a brief delay
    setTimeout(() => {
      if (tableRef.current && newHighlighted.size > 0) {
        const firstHighlightedSymbol = Array.from(newHighlighted)[0];
        const firstRow = tableRef.current.querySelector(`[data-symbol="${firstHighlightedSymbol}"]`);
        if (firstRow) {
          firstRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      
      // Remove highlighting after 3 seconds
      setTimeout(() => {
        setHighlightedSymbols(new Set());
      }, 3000);
    }, 100);
  };

  // Helper to calculate trigger price
  const calculateTriggerPrice = (
    trigger_value: number,
    value_type: 'PERCENT' | 'ABSOLUTE' | undefined,
    reference_price?: number
  ): number | null => {
    if (!reference_price || !trigger_value || trigger_value <= 0) return null;
    let price: number;
    if (value_type === 'ABSOLUTE') {
      price = reference_price - trigger_value;
    } else {
      price = reference_price * (1 - trigger_value / 100);
    }
    return Number(price.toFixed(2)); // round to 2 decimals
  };

  // Inline editing functions for holdings
  const startEdit = (holdingId: string, field: 'quantity' | 'average_price', currentValue: number) => {
    const key = `${holdingId}-${field}`;
    setEditingFields(prev => new Set(prev).add(key));
    setEditingValues(prev => ({
      ...prev,
      [holdingId]: {
        ...prev[holdingId],
        [field]: currentValue.toString()
      }
    }));
  };

  const cancelEdit = (holdingId: string, field: 'quantity' | 'average_price') => {
    const key = `${holdingId}-${field}`;
    setEditingFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
    setEditingValues(prev => {
      const newValues = { ...prev };
      if (newValues[holdingId]) {
        delete newValues[holdingId][field];
        if (Object.keys(newValues[holdingId]).length === 0) {
          delete newValues[holdingId];
        }
      }
      return newValues;
    });
  };

  const saveEdit = async (holdingId: string, field: 'quantity' | 'average_price', originalValue: number) => {
    const newValue = editingValues[holdingId]?.[field];
    
    if (!newValue || newValue.trim() === '') {
      cancelEdit(holdingId, field);
      return;
    }

    const numericValue = parseFloat(newValue);
    
    // If value hasn't changed, just cancel edit
    if (numericValue === originalValue) {
      cancelEdit(holdingId, field);
      return;
    }

    // Validate the new value
    if (isNaN(numericValue) || numericValue <= 0) {
      setError(`${field === 'quantity' ? 'Quantity' : 'Buy price'} must be a positive number`);
      return;
    }

    const key = `${holdingId}-${field}`;
    setUpdatingFields(prev => new Set(prev).add(key));
    setError(null);

    try {
      const updateData = { [field]: numericValue };
      const response = await apiService.holdings.update(holdingId, updateData);
      
      if (response.success) {
        // Remove from editing state
        cancelEdit(holdingId, field);
        
        // Refresh the holdings data
        if (onBulkSaved) {
          onBulkSaved();
        }
      } else {
        setError(`Failed to update ${field}: ${response.error || 'Unknown error'}`);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || `Failed to update ${field}`;
      setError(errorMessage);
    } finally {
      setUpdatingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const updateEditingValue = (holdingId: string, field: 'quantity' | 'average_price', value: string) => {
    setEditingValues(prev => ({
      ...prev,
      [holdingId]: {
        ...prev[holdingId],
        [field]: value
      }
    }));
  };

  // Render editable field component
  const renderEditableField = (
    holdingId: string, 
    field: 'quantity' | 'average_price', 
    value: number, 
    formatValue: (val: number) => string,
    isUserAdded: boolean = false,
    isMobile: boolean = false
  ) => {
    const key = `${holdingId}-${field}`;
    const isEditing = editingFields.has(key);
    const isUpdating = updatingFields.has(key);
    const editValue = editingValues[holdingId]?.[field] || '';

    if (!isUserAdded) {
      // Non-user-added holdings are not editable
      return <span>{formatValue(value)}</span>;
    }

    if (isEditing) {
      const editContainerClass = isMobile 
        ? "flex items-center justify-center gap-1" 
        : "flex items-center gap-1";
      
      return (
        <div className={editContainerClass} onClick={(e) => e.stopPropagation()}>
          <input
            type="number"
            value={editValue}
            onChange={(e) => updateEditingValue(holdingId, field, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                saveEdit(holdingId, field, value);
              } else if (e.key === 'Escape') {
                cancelEdit(holdingId, field);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-20 px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            step={field === 'average_price' ? '0.01' : '1'}
            min="0"
            autoFocus
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              saveEdit(holdingId, field, value);
            }}
            disabled={isUpdating}
            className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
            title="Save"
          >
            {isUpdating ? (
              <div className="w-3 h-3 border border-green-600 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              cancelEdit(holdingId, field);
            }}
            disabled={isUpdating}
            className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
            title="Cancel"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      );
    }

    // Determine container classes based on context
    const containerClass = isMobile 
      ? "flex items-center justify-center gap-1 group" 
      : "flex items-center gap-1 group";

    return (
      <div className={containerClass}>
        <span>{formatValue(value)}</span>
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent row expansion when clicking edit button
            startEdit(holdingId, field, value);
          }}
          className={`p-1 text-gray-400 hover:text-blue-600 transition-opacity ${
            isMobile ? 'opacity-70' : 'opacity-0 group-hover:opacity-100'
          }`}
          title={`Edit ${field === 'quantity' ? 'quantity' : 'buy price'}`}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      </div>
    );
  };

  // Save All: only save new/edited rules
  const handleSaveAll = async () => {
    try {
      const rulesArray = Object.values(ruleDrafts)
        .filter(r => r.trigger_value && r.trigger_value > 0)
        .map(d => {
          // Find the account in the aggregated holdings
          const holding = holdings.find(h => h.symbol === d.symbol && h.exchange === d.exchange);
          const account = holding?.accounts.find(acc => acc.broker_account_id === d.target_account_id);
          const ltp = account?.current_price;
          
          if (!ltp || ltp <= 0) {
            throw new Error(`Current price not available for ${d.symbol}. Please refresh and try again.`);
          }
          
          const computedPrice = calculateTriggerPrice(d.trigger_value!, d.value_type as any, ltp);
          
          if (!computedPrice || computedPrice <= 0) {
            throw new Error(`Invalid trigger price calculated for ${d.symbol}. Please check your input.`);
          }
          
          // Frontend validation for price range
          const minAllowedPrice = ltp * 0.1; // 10% of current price
          const maxAllowedPrice = ltp * 2.0; // 200% of current price

          if (computedPrice < minAllowedPrice || computedPrice > maxAllowedPrice) {
            throw new Error(`Rule is not allowed for ${d.symbol}. Trigger price (₹${computedPrice.toFixed(2)}) must be between ₹${minAllowedPrice.toFixed(2)} and ₹${maxAllowedPrice.toFixed(2)} based on current price (₹${ltp.toFixed(2)}).`);
          }
          
          return { ...d, computed_trigger_price: computedPrice ?? undefined };
        });
      if (rulesArray.length === 0) {
        alert('No valid rules to save. Ensure trigger values are > 0');
        return;
      }

      // Call bulk endpoint
      await apiService.positionRules.bulkCreate(rulesArray);

      // Inform parent to refresh rules list
      if (onBulkSaved) onBulkSaved();
      setRuleDrafts({});
      setOpenFormKeys(new Set());
      setError(null); // Clear any previous errors
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to save rules';
      console.error('Save all error', err);
      
      // Show more user-friendly error message
      if (msg.includes('No price data available')) {
        setError(`${msg}\n\nPlease ensure the stock has recent price data before creating rules. You may need to refresh the page or wait for price updates.`);
      } else if (msg.includes('Failed to fetch price data')) {
        setError(`${msg}\n\nThere was an issue fetching price data. Please try again or contact support if the problem persists.`);
      } else {
        setError(msg);
      }
    }
  };

  // Filter and sort
  const filteredAndSortedHoldings = React.useMemo(() => {
    let filtered = holdings;
    if (searchTerm) {
      filtered = filtered.filter(holding =>
        holding.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (holding.company_name && holding.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (selectedAccount !== 'all') {
      filtered = filtered.filter(holding =>
        holding.accounts.some(acc => String(acc.broker_account_id) === selectedAccount)
      );
    }
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      switch (sortField) {
        case 'symbol': aValue = a.symbol; bValue = b.symbol; break;
        case 'total_quantity': aValue = a.total_quantity; bValue = b.total_quantity; break;
        case 'weighted_avg_price': aValue = a.weighted_avg_price; bValue = b.weighted_avg_price; break;
        case 'total_current_value': aValue = a.total_current_value; bValue = b.total_current_value; break;
        case 'total_pnl': aValue = a.total_pnl; bValue = b.total_pnl; break;
        case 'total_pnl_percent': aValue = a.total_pnl_percent; bValue = b.total_pnl_percent; break;
        case 'active_rules_count': aValue = a.active_rules_count; bValue = b.active_rules_count; break;
        default: aValue = a.symbol; bValue = b.symbol;
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return filtered;
  }, [holdings, searchTerm, selectedAccount, sortField, sortDirection]);

  const toggleSymbolExpansion = (symbol: string, exchange: string) => {
    const key = `${symbol}-${exchange}`;
    const newSet = new Set(expandedSymbols);
    if (newSet.has(key)) newSet.delete(key); else newSet.add(key);
    setExpandedSymbols(newSet);
  };

  // Inline Add Rule form handlers
  const handleShowAddRule = (symbol: string, exchange: string, accountId: number) => {
    const key = `${symbol}-${exchange}-${accountId}`;
    setOpenFormKeys(prev => new Set(prev).add(key));
    setRuleDrafts(prev => {
      if (prev[key]) return prev; // already exists
      return {
        ...prev,
        [key]: {
          symbol,
          exchange,
          target_account_id: accountId,
          rule_scope: 'specific_account',
          // action_type: 'SELL', // Temporarily disabled - broker integration required
          action_type: 'ALERT', // Default to ALERT until broker integration is ready
          trigger_type: 'TRAILING_STOP_LOSS',
          trigger_value: 7,
          value_type: 'PERCENT',
        },
      };
    });
  };
  const handleCancelAddRule = (key: string) => {
    setOpenFormKeys(prev => {
      const s = new Set(prev);
      s.delete(key);
      return s;
    });
    setRuleDrafts(prev => {
      const { [key]: removed, ...rest } = prev;
      return rest;
    });
  };
  const handleAddRuleSubmit = async (key: string, e: React.FormEvent) => {
    e.preventDefault();
    const draft = ruleDrafts[key];
    if (!draft) return;

    if (!draft.trigger_value || draft.trigger_value <= 0) {
      alert('Please enter a valid trigger value greater than 0');
      return;
    }

    // Find LTP for this holding/account to set initial trigger price
    const holding = holdings.find(h => h.symbol === draft.symbol && h.exchange === draft.exchange);
    const account = holding?.accounts.find(acc => acc.broker_account_id === draft.target_account_id);
    const ltp = account?.current_price;

    if (!ltp || ltp <= 0) {
      setError('Current price is not available for this stock.\n\nPlease ensure the stock has recent price data before creating rules. You may need to refresh the page or wait for price updates.');
      return;
    }

    const computedPrice = calculateTriggerPrice(draft.trigger_value!, draft.value_type as any, ltp);

    if (!computedPrice || computedPrice <= 0) {
      alert('Invalid trigger price calculated. Please check your input.');
      return;
    }

    // Frontend validation for price range
    const minAllowedPrice = ltp * 0.1; // 10% of current price
    const maxAllowedPrice = ltp * 2.0; // 200% of current price

    if (computedPrice < minAllowedPrice || computedPrice > maxAllowedPrice) {
      alert(`Rule is not allowed for this price. Trigger price (₹${computedPrice.toFixed(2)}) must be between ₹${minAllowedPrice.toFixed(2)} and ₹${maxAllowedPrice.toFixed(2)} based on current price (₹${ltp.toFixed(2)}).`);
      return;
    }

    await onAddRule({ ...draft, computed_trigger_price: computedPrice ?? undefined });

    handleCancelAddRule(key);
    setError(null); // Clear any previous errors
  };

  // Inline Rule List
  const renderRuleList = (symbol: string, exchange: string, accountId: number) => {
    const ruleKey = `${symbol}-${exchange}-${accountId}`;
    const accountRules = rules.filter(rule => rule.symbol === symbol && rule.exchange === exchange && rule.target_account_id === accountId);
    const draftRule = ruleDrafts[ruleKey];

    if (accountRules.length === 0 && !draftRule) {
      return <div className="text-gray-400 text-sm py-2">No rules for this account.</div>;
    }

    return (
            <div className="space-y-3 py-2">
        {accountRules.map(rule => {
          const triggerPrice = rule.computed_trigger_price;
          const stopLossType = rule.trigger_type === 'TRAILING_STOP_LOSS' ? 'trailing' : 'fixed';
          const stopLossValue = `${rule.trigger_value}${rule.value_type === 'PERCENT' ? '%' : ''}`;
          
          return (
            <div key={rule.id} className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                {/* Bell Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C10.9 2 10 2.9 10 4V4.29C7.03 5.17 5 7.9 5 11V16L3 18V19H21V18L19 16V11C19 7.9 16.97 5.17 14 4.29V4C14 2.9 13.1 2 12 2ZM10 21C10 22.1 10.9 23 12 23S14 22.1 14 21H10Z"/>
                  </svg>
                </div>
                
                {/* Rule Content */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">
                    <span className="font-medium">{stopLossValue} {stopLossType} stop loss alert</span> has been set. 
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${
                      rule.is_active ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {rule.is_active ? 'Active' : 'Triggered'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    You'll be notified when the price crosses below <span className="font-medium text-gray-900">₹{triggerPrice ? triggerPrice.toFixed(2) : 'N/A'}</span>
                  </div>
                </div>
                
                {/* Delete Button */}
                <button
                  type="button"
                  className="flex-shrink-0 text-gray-400 hover:text-red-600 transition-colors p-1"
                  onClick={(e) => { e.preventDefault(); onDeleteRule(rule.id); }}
                  title="Delete Rule"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Inline Add Rule form
  const renderAddRuleForm = (symbol: string, exchange: string, accountId: number) => {
    const key = `${symbol}-${exchange}-${accountId}`;
    if (!openFormKeys.has(key)) return null;
    return (
      <form className="bg-white border rounded p-4 my-2 shadow-sm" onSubmit={(e) => handleAddRuleSubmit(key, e)}>
        {/* Desktop Layout */}
        <div className="hidden lg:block text-sm">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {/* Action toggle */}
            <div className="flex rounded-md overflow-hidden border">
              {/* SELL action temporarily disabled - broker integration required */}
              {/* <button type="button" className={`px-4 py-1 font-semibold ${ruleDrafts[key]?.action_type === 'SELL' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], action_type: 'SELL' } })); }}>Sell</button> */}
              <button type="button" className={`px-4 py-1 font-semibold ${ruleDrafts[key]?.action_type === 'ALERT' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], action_type: 'ALERT' } })); }}>Alert</button>
            </div>

            <span className="mx-1">{ruleDrafts[key]?.action_type === 'ALERT' ? 'me' : ''} when last traded price crosses</span>

            {/* Value input */}
            <input type="text" pattern="[0-9]*" inputMode="numeric" className="border rounded px-2 py-1 w-20" value={ruleDrafts[key]?.trigger_value ?? ''} onChange={(e) => { const v = Number(e.target.value.replace(/[^0-9.]/g, '')); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], trigger_value: v } })); }} />

            {/* Value type */}
            <div className="flex rounded-md overflow-hidden border">
              <button type="button" className={`px-3 py-1 ${ruleDrafts[key]?.value_type === 'PERCENT' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], value_type: 'PERCENT' } })); }}>%</button>
              <button type="button" className={`px-3 py-1 ${ruleDrafts[key]?.value_type === 'ABSOLUTE' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], value_type: 'ABSOLUTE' } })); }}>
                ₹
              </button>
            </div>

            {/* Stop loss type */}
            <div className="flex rounded-md overflow-hidden border">
              <button type="button" className={`px-4 py-1 ${ruleDrafts[key]?.trigger_type === 'TRAILING_STOP_LOSS' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], trigger_type: 'TRAILING_STOP_LOSS' } })); }}>Trailing</button>
              <button type="button" className={`px-4 py-1 ${ruleDrafts[key]?.trigger_type === 'FIXED_STOP_LOSS' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], trigger_type: 'FIXED_STOP_LOSS' } })); }}>Fixed</button>
            </div>

            <span className="mx-1">below current price</span>

            {/* buttons */}
            <button type="submit" className="ml-auto btn-primary px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">Save Rule</button>
            <button type="button" className="btn-secondary px-3 py-1 rounded border" onClick={(e) => { e.preventDefault(); handleCancelAddRule(key); }}>Cancel</button>
          </div>

          {/* Explanation text - moved to separate line */}
          <div className="text-xs text-gray-500 italic pl-2">
            {ruleDrafts[key]?.trigger_type === 'TRAILING_STOP_LOSS' 
              ? "The alert level will move up if the price moves up, so you can keep more of your profits"
              : "The alert level is fixed and does not move with price"
            }
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden space-y-3 text-sm">
          {/* Row 1: Action Type */}
          <div className="flex items-center gap-2">
            <span className="text-gray-600 min-w-16">Action:</span>
            <div className="flex rounded-md overflow-hidden border flex-1">
              <button type="button" className={`px-4 py-2 font-semibold flex-1 ${ruleDrafts[key]?.action_type === 'ALERT' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], action_type: 'ALERT' } })); }}>Alert me</button>
            </div>
          </div>

          {/* Row 2: Trigger Value and Type */}
          <div className="flex items-center gap-2">
            <span className="text-gray-600 min-w-16">When price drops:</span>
            <div className="flex gap-2 flex-1">
              <input 
                type="text" 
                pattern="[0-9]*" 
                inputMode="numeric" 
                className="border rounded px-3 py-2 w-20 text-center" 
                value={ruleDrafts[key]?.trigger_value ?? ''} 
                onChange={(e) => { const v = Number(e.target.value.replace(/[^0-9.]/g, '')); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], trigger_value: v } })); }} 
                placeholder="7"
              />
              <div className="flex rounded-md overflow-hidden border">
                <button type="button" className={`px-3 py-2 ${ruleDrafts[key]?.value_type === 'PERCENT' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], value_type: 'PERCENT' } })); }}>%</button>
                <button type="button" className={`px-3 py-2 ${ruleDrafts[key]?.value_type === 'ABSOLUTE' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], value_type: 'ABSOLUTE' } })); }}>₹</button>
              </div>
            </div>
          </div>

          {/* Row 3: Stop Loss Type */}
          <div className="flex items-center gap-2">
            <span className="text-gray-600 min-w-16">Type:</span>
            <div className="flex rounded-md overflow-hidden border flex-1">
              <button type="button" className={`px-4 py-2 flex-1 ${ruleDrafts[key]?.trigger_type === 'TRAILING_STOP_LOSS' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], trigger_type: 'TRAILING_STOP_LOSS' } })); }}>Trailing</button>
              <button type="button" className={`px-4 py-2 flex-1 ${ruleDrafts[key]?.trigger_type === 'FIXED_STOP_LOSS' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`} onClick={(e) => { e.preventDefault(); setRuleDrafts(prev => ({ ...prev, [key]: { ...prev[key], trigger_type: 'FIXED_STOP_LOSS' } })); }}>Fixed</button>
            </div>
          </div>

          {/* Explanation text */}
          <div className="text-xs text-gray-500 italic px-2">
            {ruleDrafts[key]?.trigger_type === 'TRAILING_STOP_LOSS' 
              ? "The alert level will move up if the price moves up, so you can keep more of your profits"
              : "The alert level is fixed and does not move with price"
            }
          </div>

          {/* Row 4: Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button type="button" className="flex-1 px-4 py-2 text-sm rounded border bg-white text-gray-700 hover:bg-gray-50" onClick={(e) => { e.preventDefault(); handleCancelAddRule(key); }}>Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">Save Rule</button>
          </div>
        </div>
      </form>
    );
  };



  useImperativeHandle(ref, () => ({
    triggerMagicSetup: handleMagicSetup
  }));

  return (
    <div className="space-y-4">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700 whitespace-pre-line">{error}</div>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleMagicSetup}
            className={`px-4 py-2 text-sm rounded-md flex items-center gap-2 transition-all duration-200 font-medium ${holdings.length === 0 ? 'bg-gray-300 text-gray-400 cursor-not-allowed' : 'text-white shadow-md hover:shadow-lg'}`}
            style={holdings.length === 0 ? {} : { backgroundColor: '#8963b8' }}
            onMouseEnter={(e) => {
              if (holdings.length > 0) {
                e.currentTarget.style.backgroundColor = '#6c4c8f';
              }
            }}
            onMouseLeave={(e) => {
              if (holdings.length > 0) {
                e.currentTarget.style.backgroundColor = '#8963b8';
              }
            }}
            disabled={holdings.length === 0}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Price Guard
          </button>
          {Object.keys(ruleDrafts).length > 0 && (
            <button
              type="button"
              onClick={handleSaveAll}
              className="px-3 py-1 text-white text-sm rounded flex items-center gap-1"
              style={{ backgroundColor: '#6570c2' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5a64b0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#6570c2';
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save All
            </button>
          )}
        </div>
        
        {onAddNew && (
          <button
            type="button"
            onClick={onAddNew}
            className="px-4 py-2 text-sm text-white rounded-md flex items-center gap-2 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
            style={{ backgroundColor: '#6570c2' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#5a64b0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#6570c2';
            }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add New
          </button>
        )}
      </div>

      {/* Inline Form Slot */}
      {inlineFormSlot}

      {/* Holdings Display */}
      <div className="relative" ref={tableRef}>
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto bg-white rounded shadow border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => onSort('symbol')}>Symbol</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => onSort('total_quantity')}>Total Qty</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => onSort('weighted_avg_price')}>Buy Price</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">LTP</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => onSort('total_pnl')}>P&L</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => onSort('total_pnl_percent')}>Gain %</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer" onClick={() => onSort('active_rules_count')}>Rules</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedHoldings.map(holding => {
                const key = `${holding.symbol}-${holding.exchange}`;
                const isExpanded = expandedSymbols.has(key);
                // Calculate LTP for symbol row as weighted average of current_price
                const ltp = holding.accounts.reduce((sum, acc) => sum + (acc.current_price || 0) * acc.quantity, 0) / (holding.total_quantity || 1);
                return (
                  <React.Fragment key={key}>
                    <tr 
                      className={`bg-white hover:bg-gray-50 cursor-pointer transition-colors duration-300 ${
                        highlightedSymbols.has(key) ? 'bg-yellow-100 border-2 border-yellow-400 shadow-lg' : ''
                      }`} 
                      onClick={() => toggleSymbolExpansion(holding.symbol, holding.exchange)}
                      data-symbol={key}
                    >
                      <td className="px-4 py-2 font-medium text-gray-800">{holding.symbol}</td>
                      <td className="px-4 py-2 text-center text-gray-600 font-normal">
                        {holding.accounts.length === 1 && holding.accounts[0].broker_type === 'USER_ADDED' ? (
                          renderEditableField(
                            holding.accounts[0].id || '', 
                            'quantity', 
                            holding.accounts[0].quantity, 
                            (val) => val.toString(),
                            true,
                            false // isMobile = false
                          )
                        ) : (
                          holding.total_quantity
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600 font-normal">
                        {holding.accounts.length === 1 && holding.accounts[0].broker_type === 'USER_ADDED' ? (
                          renderEditableField(
                            holding.accounts[0].id || '', 
                            'average_price', 
                            holding.accounts[0].average_price, 
                            (val) => `₹${val.toFixed(2)}`,
                            true,
                            false // isMobile = false
                          )
                        ) : (
                          `₹${holding.weighted_avg_price.toFixed(2)}`
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600 font-normal">{ltp ? `₹${ltp.toFixed(2)}` : '-'}</td>
                      <td className={`px-4 py-2 font-normal ${holding.total_pnl >= 0 ? 'text-green-700' : 'text-red-800'}`}>{holding.total_pnl >= 0 ? '+' : ''}₹{holding.total_pnl.toFixed(2)}</td>
                      <td className={`px-4 py-2 font-normal ${holding.total_pnl_percent >= 0 ? 'text-green-700' : 'text-red-800'}`}>{holding.total_pnl_percent >= 0 ? '+' : ''}{holding.total_pnl_percent.toFixed(2)}%</td>
                      <td className="px-4 py-2">
                        {holding.active_rules_count > 0 ? (
                          <span className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">{holding.active_rules_count}</span>
                        ) : (
                          <button 
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded text-xs font-medium transition-colors"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              // Expand the holding and show add rule for the first account
                              if (holding.accounts.length > 0) {
                                toggleSymbolExpansion(holding.symbol, holding.exchange);
                                setTimeout(() => {
                                  handleShowAddRule(holding.symbol, holding.exchange, holding.accounts[0].broker_account_id);
                                }, 100);
                              }
                            }}
                            title="Add rule"
                          >
                            + Add
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end items-center gap-2">
                          <button className="text-blue-600 hover:text-blue-800 flex items-center" onClick={(e) => { e.stopPropagation(); toggleSymbolExpansion(holding.symbol, holding.exchange); }}>
                            <svg
                              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {onDeleteAggregatedHolding && (
                            <button
                              className="ml-2 text-xs text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onDeleteAggregatedHolding(holding.symbol, holding.exchange);
                              }}
                              title={`Delete ${holding.symbol}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="bg-gray-50 px-4 py-2">
                          <div className="space-y-4">
                            {holding.accounts.map(account => (
                              <div key={account.broker_account_id} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm mb-3">
                                {(() => {
                                  const accountRules = rules.filter(rule => rule.symbol === holding.symbol && rule.exchange === holding.exchange && rule.target_account_id === account.broker_account_id);
                                  const hasRules = accountRules.length > 0;
                                  
                                  return (
                                    <div className={`flex items-center ${hasRules ? 'justify-between' : 'gap-4'}`}>
                                      <div className="flex items-center gap-4">
                                        {account.broker_type !== 'USER_ADDED' && account.broker_name && (
                                          <span className="font-semibold text-sm">
                                            Broker: {account.broker_name}
                                            {account.broker_type ? ` (${account.broker_type})` : ''}
                                          </span>
                                        )}
                                        {holding.accounts.length > 1 && (
                                          <>
                                            <span className="text-xs text-gray-500">
                                              Qty: {renderEditableField(
                                                account.id || '', 
                                                'quantity', 
                                                account.quantity, 
                                                (val) => val.toString(),
                                                account.broker_type === 'USER_ADDED',
                                                false // isMobile = false (desktop expanded section)
                                              )}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              Buy: {renderEditableField(
                                                account.id || '', 
                                                'average_price', 
                                                account.average_price, 
                                                (val) => `₹${val.toFixed(2)}`,
                                                account.broker_type === 'USER_ADDED',
                                                false // isMobile = false (desktop expanded section)
                                              )}
                                            </span>
                                            <span className="text-xs text-gray-500">LTP: {account.current_price ? `₹${account.current_price.toFixed(2)}` : '-'}</span>
                                            <span className="text-xs text-gray-500">P&L: <span className={`font-normal ${account.pnl && account.pnl >= 0 ? 'text-green-700' : 'text-red-800'}`}>{account.pnl && account.pnl >= 0 ? '+' : ''}₹{account.pnl?.toFixed(2)}</span></span>
                                            <span className={`text-xs font-normal ${account.pnl_percent !== undefined && account.pnl_percent >= 0 ? 'text-green-700' : 'text-red-800'}`}>Gain: {account.pnl_percent !== undefined ? (account.pnl_percent >= 0 ? '+' : '') + account.pnl_percent.toFixed(2) + '%' : '-'}</span>
                                          </>
                                        )}
                                      </div>
                                      {hasRules && (
                                        <button 
                                          className="text-xs px-2 py-1 rounded text-blue-600 hover:bg-blue-50 hover:text-blue-800 transition-colors flex items-center gap-1" 
                                          onClick={(e) => { e.preventDefault(); handleShowAddRule(holding.symbol, holding.exchange, account.broker_account_id); }}
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                          </svg>
                                          Add Rule
                                        </button>
                                      )}
                                    </div>
                                  );
                                })()}
                                {/* Inline rule list */}
                                {renderRuleList(holding.symbol, holding.exchange, account.broker_account_id)}
                                {/* Inline add rule form */}
                                {renderAddRuleForm(holding.symbol, holding.exchange, account.broker_account_id)}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-3">
          {filteredAndSortedHoldings.map(holding => {
            const key = `${holding.symbol}-${holding.exchange}`;
            const isExpanded = expandedSymbols.has(key);
            const ltp = holding.accounts.reduce((sum, acc) => sum + (acc.current_price || 0) * acc.quantity, 0) / (holding.total_quantity || 1);
            
            return (
              <div key={key} className={`bg-white rounded-lg shadow border transition-all duration-300 ${
                highlightedSymbols.has(key) ? 'bg-yellow-50 border-yellow-400 shadow-lg' : ''
              }`} data-symbol={key}>
                {/* Card Header */}
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => toggleSymbolExpansion(holding.symbol, holding.exchange)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium text-gray-800">{holding.symbol}</h3>
                      {holding.active_rules_count > 0 && (
                        <span className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                          {holding.active_rules_count} rules
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {holding.active_rules_count === 0 && (
                        <button 
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            if (holding.accounts.length > 0) {
                              toggleSymbolExpansion(holding.symbol, holding.exchange);
                              setTimeout(() => {
                                handleShowAddRule(holding.symbol, holding.exchange, holding.accounts[0].broker_account_id);
                              }, 100);
                            }
                          }}
                          title="Add rule"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      )}
                      <button className="text-blue-600 hover:text-blue-800 p-1">
                        <svg
                          className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      {onDeleteAggregatedHolding && (
                        <button
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDeleteAggregatedHolding(holding.symbol, holding.exchange);
                          }}
                          title={`Delete ${holding.symbol}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Row 1: Headers */}
                  <div className="grid grid-cols-3 gap-4 mb-1 text-xs font-medium text-gray-500 uppercase">
                    <div className="text-center">Qty</div>
                    <div className="text-center">Buy Price</div>
                    <div className="text-center">Gain %</div>
                  </div>

                  {/* Row 1: Values */}
                  <div className="grid grid-cols-3 gap-4 mb-3 text-sm font-normal">
                    <div className="text-center text-gray-600">
                      {holding.accounts.length === 1 && holding.accounts[0].broker_type === 'USER_ADDED' ? (
                        renderEditableField(
                          holding.accounts[0].id || '', 
                          'quantity', 
                          holding.accounts[0].quantity, 
                          (val) => val.toString(),
                          true,
                          true // isMobile = true
                        )
                      ) : (
                        holding.total_quantity
                      )}
                    </div>
                    <div className="text-center text-gray-600">
                      {holding.accounts.length === 1 && holding.accounts[0].broker_type === 'USER_ADDED' ? (
                        renderEditableField(
                          holding.accounts[0].id || '', 
                          'average_price', 
                          holding.accounts[0].average_price, 
                          (val) => `₹${val.toFixed(2)}`,
                          true,
                          true // isMobile = true
                        )
                      ) : (
                        `₹${holding.weighted_avg_price.toFixed(2)}`
                      )}
                    </div>
                    <div className={`text-center font-normal ${holding.total_pnl_percent >= 0 ? 'text-green-700' : 'text-red-800'}`}>
                      {holding.total_pnl_percent >= 0 ? '+' : ''}{holding.total_pnl_percent.toFixed(2)}%
                    </div>
                  </div>

                  {/* Row 2: Headers */}
                  <div className="grid grid-cols-3 gap-4 mb-1 text-xs font-medium text-gray-500 uppercase">
                    <div className="text-center">Invested</div>
                    <div className="text-center">LTP</div>
                    <div className="text-center">Net P&L</div>
                  </div>

                  {/* Row 2: Values */}
                  <div className="grid grid-cols-3 gap-4 text-sm font-normal">
                    <div className="text-center text-gray-600 font-normal">₹{holding.total_investment.toFixed(2)}</div>
                    <div className="text-center text-gray-600 font-normal">{ltp ? `₹${ltp.toFixed(2)}` : '-'}</div>
                    <div className={`text-center font-normal ${holding.total_pnl >= 0 ? 'text-green-700' : 'text-red-800'}`}>
                      {holding.total_pnl >= 0 ? '+' : ''}₹{holding.total_pnl.toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Expanded Account Details */}
                {isExpanded && (
                  <div className="bg-gray-50 p-4">
                    <div className="space-y-4">
                      {holding.accounts.map(account => (
                        <div key={account.broker_account_id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                          <div className={`flex items-center ${account.broker_type !== 'USER_ADDED' && account.broker_name ? 'justify-between' : 'justify-end'} mb-3`}>
                            {account.broker_type !== 'USER_ADDED' && account.broker_name && (
                              <span className="font-semibold text-sm">
                                {account.broker_name}
                                {account.broker_type ? ` (${account.broker_type})` : ''}
                              </span>
                            )}
                            {(() => {
                              const accountRules = rules.filter(rule => rule.symbol === holding.symbol && rule.exchange === holding.exchange && rule.target_account_id === account.broker_account_id);
                              return accountRules.length > 0 ? (
                                <button 
                                  className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1" 
                                  onClick={(e) => { e.preventDefault(); handleShowAddRule(holding.symbol, holding.exchange, account.broker_account_id); }}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                  Add Rule
                                </button>
                              ) : null;
                            })()} 
                          </div>
                          
                          {holding.accounts.length > 1 && (
                            <>
                              {/* Account-specific data in same mobile format */}
                              <div className="grid grid-cols-3 gap-4 mb-1 text-xs font-medium text-gray-500 uppercase">
                                <div className="text-center">Qty</div>
                                <div className="text-center">Buy Price</div>
                                <div className="text-center">Gain %</div>
                              </div>
                              <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                                <div className="text-center">
                                  {renderEditableField(
                                    account.id || '', 
                                    'quantity', 
                                    account.quantity, 
                                    (val) => val.toString(),
                                    account.broker_type === 'USER_ADDED',
                                    true // isMobile = true (mobile expanded section)
                                  )}
                                </div>
                                <div className="text-center">
                                  {renderEditableField(
                                    account.id || '', 
                                    'average_price', 
                                    account.average_price, 
                                    (val) => `₹${val.toFixed(2)}`,
                                    account.broker_type === 'USER_ADDED',
                                    true // isMobile = true (mobile expanded section)
                                  )}
                                </div>
                                <div className={`text-center font-normal ${account.pnl_percent !== undefined && account.pnl_percent >= 0 ? 'text-green-700' : 'text-red-800'}`}>
                                  {account.pnl_percent !== undefined ? (account.pnl_percent >= 0 ? '+' : '') + account.pnl_percent.toFixed(2) + '%' : '-'}
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-4 mb-1 text-xs font-medium text-gray-500 uppercase">
                                <div className="text-center">Invested</div>
                                <div className="text-center">LTP</div>
                                <div className="text-center">Net P&L</div>
                              </div>
                              <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                                <div className="text-center text-gray-600 font-normal">₹{account.purchase_value.toFixed(2)}</div>
                                <div className="text-center text-gray-600 font-normal">{account.current_price ? `₹${account.current_price.toFixed(2)}` : '-'}</div>
                                <div className={`text-center font-normal ${account.pnl && account.pnl >= 0 ? 'text-green-700' : 'text-red-800'}`}>
                                  {account.pnl && account.pnl >= 0 ? '+' : ''}₹{account.pnl?.toFixed(2)}
                                </div>
                              </div>
                            </>
                          )}
                          
                          {/* Rules List */}
                          {renderRuleList(holding.symbol, holding.exchange, account.broker_account_id)}
                          {/* Add Rule Form - Mobile Optimized */}
                          {renderAddRuleForm(holding.symbol, holding.exchange, account.broker_account_id)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default EnhancedHoldingsTable; 